import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const NOTI_DOC = doc(db, 'settings', 'notificationMessages');

const DEFAULT_MESSAGES = {
  board: '수업 게시판에 새 글이 등록되었습니다',
  material: '새 수업자료가 등록되었습니다',
  video: '새 수업영상이 등록되었습니다',
  assignment: '새 과제가 등록되었습니다',
  grade: '성적이 등록되었습니다',
  adminMessage: '관리자 공지 메시지가 도착했습니다',
  academyEvent: '새 학원 일정이 등록되었습니다',
  submission: '학생이 과제를 제출했습니다',
  gradeUpdate: '성적이 업데이트되었습니다',
  attendance: '출석 상태가 변경되었습니다',
};

const NOTI_LABELS = {
  board: { label: '게시판 알림', target: '학생, 학부모', trigger: 'onCreate' },
  material: { label: '수업자료 알림', target: '학생, 학부모', trigger: 'onCreate' },
  video: { label: '수업영상 알림', target: '학생, 학부모', trigger: 'onCreate' },
  assignment: { label: '과제 알림', target: '학생, 학부모', trigger: 'onCreate' },
  grade: { label: '성적 등록 알림', target: '학생, 학부모', trigger: 'onCreate' },
  adminMessage: { label: '관리자 공지 알림', target: '해당 유저', trigger: 'onCreate' },
  academyEvent: { label: '학원 일정 알림', target: '전체', trigger: 'onCreate' },
  submission: { label: '과제 제출 알림', target: '강사', trigger: 'onUpdate' },
  gradeUpdate: { label: '성적 업데이트 알림', target: '학생, 학부모', trigger: 'onUpdate' },
  attendance: { label: '출석 변경 알림', target: '학생, 학부모', trigger: 'onUpdate' },
};

const AdminNotificationsPage = () => {
  const [messages, setMessages] = useState({ ...DEFAULT_MESSAGES });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(NOTI_DOC);
        if (snap.exists()) {
          setMessages(prev => ({ ...prev, ...snap.data() }));
        }
      } catch (err) {
        console.error('알림 문구 로드 실패:', err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(NOTI_DOC, messages, { merge: true });
      alert('알림 문구가 저장되었습니다.');
    } catch (err) {
      console.error('알림 문구 저장 실패:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (key) => {
    setMessages(prev => ({ ...prev, [key]: DEFAULT_MESSAGES[key] }));
  };

  if (!loaded) return null;

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>알림 문구 설정</PageTitle>
          <PageDesc>모든 알림은 자동 발송됩니다. 각 알림의 푸시 메시지 문구를 수정할 수 있습니다.</PageDesc>
        </div>
        <SaveBtn onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </SaveBtn>
      </PageHeader>

      <Card>
        <TableHead>
          <ColLabel style={{ flex: 2 }}>알림 종류</ColLabel>
          <ColLabel style={{ flex: 1 }}>수신 대상</ColLabel>
          <ColLabel style={{ flex: 1 }}>트리거</ColLabel>
          <ColLabel style={{ flex: 4 }}>알림 문구</ColLabel>
          <ColLabel style={{ width: 60 }}>초기화</ColLabel>
        </TableHead>
        {Object.keys(NOTI_LABELS).map((key, idx, arr) => {
          const info = NOTI_LABELS[key];
          return (
            <Row key={key} $last={idx === arr.length - 1}>
              <RowCell style={{ flex: 2 }}>
                <NotiName>{info.label}</NotiName>
              </RowCell>
              <RowCell style={{ flex: 1 }}>
                <TargetBadge>{info.target}</TargetBadge>
              </RowCell>
              <RowCell style={{ flex: 1 }}>
                <TriggerBadge $type={info.trigger}>
                  {info.trigger === 'onCreate' ? '생성 시' : '변경 시'}
                </TriggerBadge>
              </RowCell>
              <RowCell style={{ flex: 4 }}>
                <MsgInput
                  value={messages[key] || ''}
                  onChange={(e) => setMessages(prev => ({ ...prev, [key]: e.target.value }))}
                />
              </RowCell>
              <RowCell style={{ width: 60, justifyContent: 'center' }}>
                <ResetBtn
                  onClick={() => handleReset(key)}
                  disabled={messages[key] === DEFAULT_MESSAGES[key]}
                  title="기본값으로 초기화"
                >
                  ↺
                </ResetBtn>
              </RowCell>
            </Row>
          );
        })}
      </Card>
    </Container>
  );
};

export default AdminNotificationsPage;

const Container = styled.div``;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
`;

const PageDesc = styled.p`
  font-size: 13px;
  color: #9CA3AF;
  margin-top: 4px;
`;

const SaveBtn = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  background: #4A7CFE;
  color: white;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  &:hover { background: #3B6DE8; }
  &:disabled { opacity: 0.5; }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #EAEDF3;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: #FAFBFC;
  border-bottom: 1px solid #EAEDF3;
`;

const ColLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid #F3F4F6'};
  &:hover { background: #FAFBFC; }
`;

const RowCell = styled.div`
  display: flex;
  align-items: center;
`;

const NotiName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const TargetBadge = styled.span`
  font-size: 12px;
  color: #6B7280;
  background: #F3F4F6;
  padding: 3px 10px;
  border-radius: 6px;
  white-space: nowrap;
`;

const TriggerBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  white-space: nowrap;
  background: ${({ $type }) => $type === 'onCreate' ? '#EEF2FF' : '#FEF3C7'};
  color: ${({ $type }) => $type === 'onCreate' ? '#4A7CFE' : '#D97706'};
`;

const MsgInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  box-sizing: border-box;
  &:focus { border-color: #4A7CFE; box-shadow: 0 0 0 3px #EEF2FF; outline: none; }
`;

const ResetBtn = styled.button`
  font-size: 18px;
  color: #9CA3AF;
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: #F3F4F6; color: #6B7280; }
  &:disabled { opacity: 0.3; cursor: default; }
`;
